/**
 * Where the screens are translated: at the moment React is handed an HTML element, the
 * text in it and the attributes a person reads or hears are put in the active language.
 * Components are untouched — they keep writing English — and it does not matter where a
 * text came from (written in the screen, kept in a helper, sent by the server): it is
 * translated when it is about to be shown.
 *
 * Only HTML elements, fragments and the router's links are looked at. Text handed to a
 * component passes through until that component puts it in an element — which is where it
 * is translated, and never before, so a component that reads its own text still reads
 * what was written.
 */
import { Fragment } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { provideTranslation } from './hook';
import { activeLanguage, prepareLanguage, t } from './language';

/** Attributes whose value is shown or read aloud (scripts/i18n-strings.cjs lists the same). */
const SPOKEN_ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt', 'label'] as const;

/** Elements whose text is code, not prose. */
const NOT_PROSE = new Set(['code', 'pre']);

/** Library components that show the text they are given; their elements are out of reach. */
const SHOWS_ITS_TEXT = new Set<unknown>([Link, NavLink]);

type Props = Record<string, unknown>;

function translatedChildren(children: unknown): unknown {
  if (typeof children === 'string') return t(children);
  if (!Array.isArray(children)) return children;
  let changed: unknown[] | null = null;
  children.forEach((child, index) => {
    if (typeof child !== 'string' && !Array.isArray(child)) return;
    const shown = translatedChildren(child);
    if (shown !== child) {
      changed ??= [...children];
      changed[index] = shown;
    }
  });
  return changed ?? children;
}

export function translatedProps<P>(type: unknown, props: P): P {
  if (activeLanguage() === 'en') return props;
  const isElement = typeof type === 'string';
  if (props === null || typeof props !== 'object') return props;
  if (!isElement && type !== Fragment && !SHOWS_ITS_TEXT.has(type)) return props;
  const written = props as Props;
  // `translate="no"` is HTML's own way of saying "this is not to be translated".
  if (isElement && (NOT_PROSE.has(type) || written.translate === 'no')) return props;

  let shown: Props | null = null;
  const children = translatedChildren(written.children);
  if (children !== written.children) shown = { ...written, children };
  for (const name of SPOKEN_ATTRIBUTES) {
    const value = written[name];
    if (typeof value !== 'string') continue;
    const said = t(value);
    if (said !== value) {
      shown ??= { ...written };
      shown[name] = said;
    }
  }
  return (shown ?? written) as P;
}

/**
 * Called once, before the first screen is built: has the dictionary of the page's
 * language at hand, and from then on the JSX runtime shows every element through
 * `translatedProps`.
 */
export async function prepareScreens(): Promise<void> {
  await prepareLanguage();
  provideTranslation(translatedProps);
}
